/**
 * @file report.component.tsx
 * @description Основной компонент отчетов для отображения аналитических данных в Task Manager приложении. Предоставляет специализированный интерфейс для генерации отчетов с возможностью ручного формирования, фильтрации данных, экспорта в Excel, древовидного отображения данных и опционального взаимодействия с строками для просмотра деталей объектов.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-11
 * @dependencies ['@features/page-modal', '@features/catalog/components', '@hooks/use-modals', 'react', '@components/ui', '@components/ui/indicators/error-list', 'axios', '@utils/table-view', '@src/api/actions/report', '@features/table', '@features/filter', '@components/ui/base/button', 'classnames', '@features/object-form', '@utils/form-object-helper', '@src/types', '@api/actions/export', '@utils/file-helper', 'moment', '@components/icons/ms-excel', '@utils/error-helper']
 * @related-files ['src/features/report/report.provider.tsx', 'src/features/report/index.js', 'src/api/actions/report.ts', 'src/api/actions/export.ts', 'src/features/catalog/components/header.component.tsx']
 */

import { PageModal } from '@components/ui/layout/page-modal';
import { Header } from '@features/catalog/components';
import { useModalQueue } from '@hooks/use-modals';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader } from '@components/ui';
import { ErrorList, ErrorListHandle } from '@components/ui/indicators/error-list';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { getDefaultCell } from '@utils/table-view';
import { ReportService } from '@src/api/actions/report';
import { Table } from '@features/table';
import { FilterModal } from '@features/filter';
import { ToolbarTags as FilterTagLine } from '@features/filter';
import { getActiveFilters, prepareFilterControlsToFetch, prepareFilterFormToFetch } from '@features/filter';
import { Button } from '@components/ui/base/button';
import { useSavedFilters } from '@features/filter';
import cn from 'classnames';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@src/utils/form-object-helper';
import { TContextObject, TBaseObjectDto, ITableItemDTO } from '@src/types';
import { ExportService } from '@api/actions/export';
import { downloadBlob } from '@utils/file-helper';
import moment from 'moment';
import { MSExcelIcon } from '@components/icons/ms-excel';
import { setErrors } from '../../utils/error-helper';

interface IReport {
    container?: HTMLDivElement;
    type: string;
    title?: string;
    columns?: any[];
    form?: any;
    getFilterControls?: any;
    closePageHandler?: any;
    onRowClick?: Function;
    onClose?: Function;
    isRowClickable?: false;
}

const defaultReportColumns = [
    {
        id: '$item',
        header: [{ text: 'Наименование' }],
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];

export const Report = ({
    container,
    title = 'Отчет',
    columns = defaultReportColumns,
    type,
    getFilterControls,
    onClose,
    isRowClickable = false
}: IReport) => {
    const { getFilters, saveFilters } = useSavedFilters(type);
    const [rows, setRows] = useState<ITableItemDTO[]>([]);
    const [isShow, setIsShow] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isFiltersChanged, setIsFiltersChanged] = useState<boolean>(false);
    const [filterControls, setFilterControls] = useState<any>(getFilterControls());
    const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
    const errorListRef = useRef<ErrorListHandle | null>(null);
    const rowsRef = useRef<ITableItemDTO[]>(rows);
    const tableRef = useRef<any>(null);
    const filterControlsRef = useRef<any>(filterControls);
    const activeFilters = useMemo(() => getActiveFilters(filterControls), [filterControls]);
    const [openedObject, setOpenedObject] = useState<TBaseObjectDto | null>(null);
    const objectEntityRef = useRef<any>(null);

    const closePageHandler = useModalQueue(() => {
        setIsShow(false);
        onClose && onClose();
    }, type);
    const [modalContainer, setModalContainer] = useState<HTMLDivElement | undefined>(container);

    useEffect(() => {
        async function tryFecthSavedFilters() {
            const savedFilters = await getFilters();
            setFilterControls(getFilterControls(savedFilters));
        }
        tryFecthSavedFilters();
        setRows([]);
    }, [type]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        filterControlsRef.current = filterControls;
    }, [filterControls]);

    const fetchData = async (filterItems: any = null) => {
        setIsFetching(true);
        const prevItems = rowsRef.current;
        const fetchFilters = filterItems
            ? prepareFilterFormToFetch(filterItems, filterControls)
            : prepareFilterControlsToFetch(filterControls);
        setRows([]);
        const response: AxiosResponse | AxiosError = await ReportService.getList(type, {
            filterItems: fetchFilters
        });

        setIsFiltersChanged(false);

        if (axios.isAxiosError(response)) {
            setErrors(response, errorListRef.current);
            setRows(prevItems);
            setIsFetching(false);
            return;
        }

        saveFilters(filterControlsRef.current);
        setRows(response.data);
        tableRef.current?.scroll(0, 0);
        setIsShow(true);
        setIsFetching(false);
    };

    useEffect(() => {
        setModalContainer(container || (document.getElementById('MainLayout') as HTMLDivElement));

        return () => {
            closePageHandler();
        };
    }, []);

    const contextMenuItems: TContextObject[] = [
        {
            label: 'Экспорт в xls',
            icon: <MSExcelIcon />,
            clickHandler: async () => {
                const fetchFilters = prepareFilterControlsToFetch(filterControls);
                const response = await ExportService.getFile(type, { filterItems: fetchFilters }, { type: 'report' });

                if (axios.isAxiosError(response)) {
                    setErrors(response, errorListRef.current);
                    return;
                }

                downloadBlob(response.data, `${moment().format('DD_MM_YYYY')}_current_report.xlsx`);
            }
        }
    ];

    if (!modalContainer) {
        return;
    }

    return (
        <PageModal container={modalContainer} closePageHandler={closePageHandler} isOpen={isShow}>
            <div className="report gap-default h-full w-full relative">
                <div className="report__wrapper absolute w-full h-full flex flex-col pr-[3.1756vmin]">
                    <Header
                        title={title}
                        contextMenuItems={contextMenuItems}
                        closePageHandler={closePageHandler}
                        filterControls={filterControls}
                        onFilterButtonClick={() => setIsShowFilterModal(true)}
                        isShowFilterModal={isShowFilterModal}
                        isCanShowModal={!isShowFilterModal}
                    />

                    <Button
                        label="Сформировать"
                        className="w-[14.9254vmin] mb-[1.0585vmin]"
                        onClick={() => fetchData()}
                    />

                    {activeFilters.length > 0 && (
                        <FilterTagLine
                            filters={activeFilters}
                            onFilterClick={() => setIsShowFilterModal(true)}
                            onClearFilters={(filterName: string | undefined) => {
                                let newFilterControls = null;

                                if (filterName) {
                                    newFilterControls = filterControls.map((filterControl: any) => {
                                        if (filterControl.name !== filterName) {
                                            return filterControl;
                                        }

                                        return { ...filterControl, value: null };
                                    });
                                } else {
                                    newFilterControls = getFilterControls();
                                }

                                setFilterControls(newFilterControls);
                                setIsFiltersChanged(true);
                            }}
                        />
                    )}

                    {isFetching ? (
                        <Loader />
                    ) : (
                        <Table
                            items={rows}
                            rowHeight={19}
                            columns={columns}
                            ref={tableRef}
                            isTree={true}
                            className={cn('overflow-x-hidden', {
                                'opacity-50': isFiltersChanged,
                                'opacity-100': !isFiltersChanged,
                                'clickable-rows': isRowClickable
                            })}
                            onRowClick={
                                isRowClickable
                                    ? (row: ITableItemDTO) => {
                                          if (row.$item.$type) {
                                              setOpenedObject(row.$item);
                                              objectEntityRef.current = getObjectEntity(row.$item.$type);
                                          }
                                      }
                                    : undefined
                            }
                        />
                    )}
                </div>

                {Boolean(openedObject) && objectEntityRef.current && (
                    <ObjectForm
                        type={openedObject?.$type}
                        initialItem={openedObject}
                        onClose={() => {
                            setOpenedObject(null);
                            objectEntityRef.current = null;
                        }}
                        {...objectEntityRef.current}
                    />
                )}

                <ErrorList ref={errorListRef} />

                {isShowFilterModal && (
                    <FilterModal
                        container={modalContainer}
                        isShow={isShowFilterModal}
                        setIsShow={setIsShowFilterModal}
                        filterControls={filterControls}
                        applyNewFilterControls={setFilterControls}
                        fetchDataHadler={fetchData}
                        itemType={type}
                    />
                )}
            </div>
        </PageModal>
    );
};
